import { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

const CreateRoomModal = ({ onClose, onSubmit, isLoading }) => {
  const [roomName, setRoomName] = useState('');
  const [maxParticipants, setMaxParticipants] = useState(8);
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!roomName.trim()) {
      setFormError('Room name is required');
      return;
    }
    
    if (maxParticipants < 2 || maxParticipants % 2 !== 0) {
      setFormError('Participants must be an even number (minimum 2)');
      return;
    }
    
    setFormError('');
    
    try {
      await onSubmit({
        roomName: roomName.trim(),
        maxParticipants
      });
      
      // Reset form
      setRoomName('');
      setMaxParticipants(8);
      
      // Close modal
      onClose();
    } catch (error) {
      console.error('Error creating room:', error);
      setFormError('Failed to create room. Please try again.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
      <div className="bg-dark-lighter w-full max-w-md rounded-lg p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white"
          disabled={isLoading}
        >
          <XMarkIcon className="h-6 w-6" />
        </button>
        
        <h2 className="text-xl font-bold text-white mb-4">Create New Room</h2>
        
        {formError && (
          <div className="mb-4 bg-red-900/30 border border-red-500 text-red-200 px-4 py-2 rounded">
            {formError}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="roomName" className="block text-gray-300 mb-2">
              Room Name
            </label>
            <input
              type="text"
              id="roomName"
              value={roomName}
              onChange={(e) => setRoomName(e.target.value)}
              placeholder="Enter room name"
              className="w-full bg-dark-lightest border border-gray-700 rounded px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={isLoading}
              required
            />
          </div>
          
          <div className="mb-6">
            <label htmlFor="maxParticipants" className="block text-gray-300 mb-2">
              Maximum Participants (must be even)
            </label>
            <select
              id="maxParticipants"
              value={maxParticipants}
              onChange={(e) => setMaxParticipants(Number(e.target.value))}
              className="w-full bg-dark-lightest border border-gray-700 rounded px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={isLoading}
            >
              <option value={2}>2 players</option>
              <option value={4}>4 players</option>
              <option value={6}>6 players</option>
              <option value={8}>8 players</option>
              <option value={10}>10 players</option>
              <option value={12}>12 players</option>
            </select>
          </div>
          
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded mr-2"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded"
              disabled={isLoading}
            >
              {isLoading ? 'Creating...' : 'Create Room'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateRoomModal;
